<template>
  <v-card :loading="loading" v-bind="$attrs" v-on="$listeners">
    <v-card-title>
      <v-row align="center">
        <v-col>
          <span class="headline">My Dealer Incentives</span>
        </v-col>
        <v-col cols="auto">
          <v-btn
            icon
            title="Export CSV"
            aria-label="Export CSV"
            :disabled="loading || isExporting || !items.length"
            @click="exportDisplayedData"
          >
            <v-icon v-if="!isExporting">mdi-download</v-icon>
            <v-progress-circular v-else indeterminate color="primary" size="20" width="2" />
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-subtitle>
      <span v-if="selectedFiscalYear && selectedFiscalYear.fiscalYearStart && selectedFiscalYear.fiscalYearEnd">
        <b>
          ({{ selectedFiscalYear.fiscalYearStart | formatDateClient("MM/DD/YYYY", selectedClient) }} -
          {{ selectedFiscalYear.fiscalYearEnd | formatDateClient("MM/DD/YYYY", selectedClient) }})
        </b>
        <br />
      </span>
      Summary of dealers reporting to you.
      <br />
      Currently displaying only dealers with payouts.
    </v-card-subtitle>

    <v-card-text>
      <v-alert v-if="exportError" type="error" text>{{ exportError }}</v-alert>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        dense
        :options.sync="options"
        :server-items-length="total"
      >
        <template v-slot:item.name="{ item }">
          <a @click.prevent="viewDealerDetails(item.organizationId)" href="#">
            {{ item.name }}
          </a>
        </template>
        <template v-slot:item.earningAmount="{ item }">
          {{ item.earningAmount || 0 | toCurrency }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "DealersIncentives",

  data() {
    return {
      loading: false,
      isExporting: false,
      exportError: "",
      items: [],
      total: 0,
      options: {
        itemsPerPage: 10,
        page: 1,
        sortDesc: [true]
      },
      headers: [
        { text: "Dealer Number", value: "organizationKey" },
        { text: "Dealer Name", value: "name" },
        { text: "Earned", value: "earningAmount" }
      ]
    };
  },
  computed: {
    ...mapGetters(["selectedParticipant", "selectedProgram", "selectedClient", "selectedFiscalYear"])
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true
    },
    selectedFiscalYear() {
      this.fetchData();
    }
  },
  methods: {
    getPayoutFilters() {
      const filters = {
        parentOrganization: { id: this.selectedParticipant.organization?.id }
      };

      if (this.selectedFiscalYear?.fiscalYearStart) {
        filters.createdAfter = this.selectedFiscalYear.fiscalYearStart;
      }
      if (this.selectedFiscalYear?.fiscalYearEnd) {
        filters.createdBefore = this.$util
          .getDateInClientTimezone(this.selectedFiscalYear.fiscalYearEnd)
          .add(1, "day")
          .toDate();
      }

      return filters;
    },
    fetchData() {
      this.loading = true;
      const filters = this.getPayoutFilters();
      const params = this.$util.buildPaginationParams(this.options);
      this.$api
        .post(`/api/payouts/search/group/byOrganization?${params.toString()}`, filters)
        .then(({ data }) => {
          this.items = data.content;
          this.total = data.totalElements;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    viewDealerDetails(dealerId) {
      this.$router.push({ name: "dealerDetails", query: { dealerId } });
    },
    async exportDisplayedData() {
      this.isExporting = true;
      this.exportError = "";
      try {
        const dealers = await this.$util.fetchPaginatedData(
          "/api/payouts/search/group/byOrganization",
          this.getPayoutFilters()
        );
        const columns = this.headers.map(header =>
          header.value === "earningAmount"
            ? { ...header, format: row => this.$util.toCurrency(row[header.value] || 0) }
            : header
        );
        this.$export.exportToCsv("my_dealer_incentives", columns, dealers);
      } catch (error) {
        this.exportError =
          this.$kawasaki.getApiErrorMessages(error?.response?.data)[0] ||
          error?.message ||
          "Unable to export the report. Please try again.";
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>
